// Fetches the canonical persisted transcript for the active session and
// mirrors it into the Zustand store via `hydrateMessages`. Legacy uses
// the same pattern (RTK Query + a setLocalMessages mirror inside
// ChatPanel) — V1 owns the same shape with TanStack Query.
//
// The fetcher runs:
//   • Once on session open (TanStack auto-fires on mount).
//   • Again whenever `useAgentForgeStream` invalidates the messages
//     query on `stream_done` / `agent_message` / `stream_error`.
//   • Every TURN_POLL_MS while a turn is in flight — the WS is the fast
//     path, not the only path. If the socket is down/reconnecting for the
//     whole turn (stale token dance on first load, proxy hiccup), the
//     poll is what surfaces the reply instead of indefinite dead air.
//
// Mount this exactly once (in the `AgentForge` umbrella) so every
// surface reads the same Zustand mirror.

import { useEffect, useRef } from 'react';
import { toStoreInputs, useMessagesQuery } from '../api/messages';
import type { ServerMessage } from '../api/messages.types';
import { useAgentForgeStore } from '../stores';

// Poll cadence while a turn is in flight. Bounded: polling stops the moment
// the tab leaves 'streaming', so the steady-state (no active turn) is zero
// requests — same as before.
const TURN_POLL_MS = 5_000;

// Escape hatch for a turn whose stream_done never arrives (worker died,
// replay window exceeded). After this long in 'streaming' the tab unlocks so
// the composer isn't held hostage by a ghost turn; any late reply still
// lands via the normal invalidation → hydration path.
const STUCK_TURN_MS = 180_000;

// The server transcript's terminal shape for a finished turn: closing
// assistant prose, or an interaction card (clarify/plan) awaiting the user.
// Mid-turn snapshots end with tool results or empty assistant tool-callers —
// those must NOT hydrate (they'd wipe the live draft and drop blocks).
const lastServerMessageTerminal = (data: ServerMessage[]): boolean => {
  const last = data[data.length - 1];
  if (!last) return false;
  if (last.role === 'assistant') return !!last.content?.trim();
  if (last.role === 'tool') {
    const toolName = (last.metadata as { tool_name?: unknown } | undefined)
      ?.tool_name;
    return typeof toolName === 'string' && toolName.startsWith('propose_');
  }
  return false;
};

export const useAgentForgeMessages = (
  sessionId: string | null | undefined
): void => {
  const hydrateMessages = useAgentForgeStore((s) => s.hydrateMessages);
  const setTabStatus = useAgentForgeStore((s) => s.setTabStatus);
  const clearStreamingDraft = useAgentForgeStore((s) => s.clearStreamingDraft);
  const tabStatus = useAgentForgeStore(
    (s) => s.openTabs.find((t) => t.sessionId === sessionId)?.status
  );

  const query = useMessagesQuery({
    sessionId,
    // Local instant-feedback placeholders ('pending-') don't exist
    // server-side — skip the fetch until migrateSession re-keys them.
    enabled: !!sessionId && !sessionId.startsWith('pending-'),
    // In-flight turns poll as a WS-independent fallback; idle sessions
    // don't poll at all (invalidation-driven, same as legacy).
    refetchIntervalMs: tabStatus === 'streaming' ? TURN_POLL_MS : undefined,
  });

  // Stamp when the active session's turn started streaming so the stuck-turn
  // escape hatch can measure elapsed time across re-renders.
  const streamingSinceRef = useRef<number | null>(null);
  useEffect(() => {
    if (tabStatus === 'streaming') {
      streamingSinceRef.current ??= Date.now();
      return undefined;
    }
    streamingSinceRef.current = null;
    return undefined;
  }, [tabStatus, sessionId]);

  // Stuck-turn watchdog: a turn whose stream_done never arrives would lock
  // the composer forever (sends are serialized on tab status). Unlock after
  // STUCK_TURN_MS — the reply, if it ever lands, still hydrates normally.
  useEffect(() => {
    if (!sessionId || tabStatus !== 'streaming') return undefined;
    const timer = window.setInterval(() => {
      const since = streamingSinceRef.current;
      if (since && Date.now() - since > STUCK_TURN_MS) {
        setTabStatus(sessionId, 'done');
        clearStreamingDraft();
      }
    }, TURN_POLL_MS);
    return () => window.clearInterval(timer);
  }, [sessionId, tabStatus, setTabStatus, clearStreamingDraft]);

  useEffect(() => {
    const data = query.data;
    if (!sessionId || !data || data.length === 0) return;

    // Never hydrate a MID-TURN snapshot. The server transcript then is a
    // partial [user, empty_assistant_tool_calls, tool] — folding tool blocks
    // into a missing-yet-final assistant turn drops them on the floor, and
    // `clearStreamingDraft()` below would wipe the live WS draft that *did*
    // have the card. But when the transcript already shows the turn's
    // TERMINAL shape (closing prose / clarify card), the turn is de-facto
    // done even if stream_done never reached us — hydrating is exactly the
    // recovery a dead socket needs.
    const state = useAgentForgeStore.getState();
    const streaming =
      state.streamingDraft?.sessionId === sessionId ||
      state.openTabs.find((t) => t.sessionId === sessionId)?.status ===
        'streaming';
    if (streaming && !lastServerMessageTerminal(data)) return;

    // Don't clobber a fresh optimistic user message before the server
    // has caught up. Two gates:
    //   1. Server must have a new turn-closing message we don't know about —
    //      a new assistant turn, or a trailing clarify/plan card (clarify-only
    //      turns end without assistant prose).
    //   2. Server must have at least as many user messages as we do —
    //      otherwise our trailing optimistic user (just typed, not yet
    //      mirrored back in the GET) would be wiped when we replace.
    const local = state.messagesBySession[sessionId] ?? [];
    const hasOptimisticUser = local.some((m) => m.role === 'user');
    if (hasOptimisticUser) {
      const knownAssistantIds = new Set(
        local.filter((m) => m.role === 'assistant').map((m) => m.id)
      );
      const serverHasNewAssistant = data.some(
        (m) => m.role === 'assistant' && !knownAssistantIds.has(m.id)
      );
      const endsWithClarify =
        data[data.length - 1]?.role === 'tool' &&
        lastServerMessageTerminal(data);
      if (!serverHasNewAssistant && !endsWithClarify) return;
      const localUserCount = local.filter((m) => m.role === 'user').length;
      const serverUserCount = data.filter((m) => m.role === 'user').length;
      if (serverUserCount < localUserCount) return;
    }

    hydrateMessages(sessionId, toStoreInputs(data));

    // Drop the WS draft in the same tick as the canonical record lands so
    // the streamed text swaps directly into the persisted message — no
    // "Thinking…" gap in between. `useAgentForgeStream` deliberately
    // leaves the draft alive on `stream_done` for this reason.
    clearStreamingDraft();

    // Flip the tab's status dot off once the canonical record has
    // landed. No-op for inline `/agent` (no tab in the open list).
    if (tabStatus === 'streaming') setTabStatus(sessionId, 'done');
  }, [
    sessionId,
    query.data,
    hydrateMessages,
    clearStreamingDraft,
    setTabStatus,
    tabStatus,
  ]);
};
